import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Building2, Users, Target, CalendarDays, MessagesSquare,
  User, Eye, Pencil, CheckCircle2, CircleDot, Circle, Sparkles, MapPin, Handshake,
} from "lucide-react";
import Seo from "../components/Seo";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOURNEY = [
  { label: "Profile Created", state: "done" },
  { label: "Discover Businesses", state: "active" },
  { label: "Express Interest", state: "active" },
  { label: "Collaborate", state: "upcoming" },
  { label: "Grow", state: "upcoming" },
];

function formatShort(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return ""; }
}

function EmptySection({ title, description, testId, icon: Icon }) {
  return (
    <div className="hub-card rounded-2xl p-8 text-center" data-testid={testId}>
      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--soft)] text-[color:var(--ink-soft)]"><Icon className="h-5 w-5" /></span>
      <h3 className="font-display mt-4 text-lg font-semibold text-[color:var(--ink)]">{title}</h3>
      <p className="mt-2 text-sm text-[color:var(--ink-soft)]">{description}</p>
      <span className="mt-4 inline-flex items-center rounded-full bg-[color:var(--soft)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">Coming soon</span>
    </div>
  );
}

const TIMELINE_ICON = { done: CheckCircle2, active: CircleDot, upcoming: Circle };

export default function MemberHome() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [business, setBusiness] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [interestCounts, setInterestCounts] = useState({ received: 0, pending: 0 });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/hub/profile`, { withCredentials: true });
        setProfile(data.profile);
        setBusiness(data.business);
      } catch { /* ignore */ }
      try {
        const { data } = await axios.get(`${API}/hub/events`, { params: { when: "upcoming" }, withCredentials: true });
        setUpcomingEvents((data.items || []).slice(0, 3));
      } catch { /* ignore */ }
      try {
        const { data } = await axios.get(`${API}/hub/interests/received`, { withCredentials: true });
        const items = data.items || [];
        setInterestCounts({
          received: items.length,
          pending: items.filter((i) => i.status === "new" || i.status === "viewed").length,
        });
      } catch { /* ignore */ }
    })();
  }, []);

  const firstName = (profile?.full_name || user?.name || "there").split(" ")[0];
  const completion = user?.profile_completion ?? profile?.profile_completion ?? 0;

  return (
    <AppLayout>
      <Seo title="My Hub" description="Your StUpEco Hub home." path="/app" />
      <div className="container-hub py-8 lg:py-12" data-testid="workspace-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--brand-teal)]">My Hub</p>
            <h1 className="font-display mt-2 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">
              Welcome back, {firstName}.
            </h1>
            <p className="mt-2 text-[color:var(--ink-soft)]">Here's what's happening around your Hub.</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Profile completion */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="hub-card rounded-2xl p-6 lg:col-span-1" data-testid="profile-completion">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">Your profile</p>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-4xl font-semibold text-[color:var(--ink)]" data-testid="profile-completion-percent">{completion}%</span>
              <span className="text-sm text-[color:var(--ink-soft)]">complete</span>
            </div>
            <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--soft)]"><div className="h-full brand-bar" style={{ width: `${completion}%` }} /></div>
            <Link to="/app/profile" data-testid="cta-complete-profile" className="group mt-5 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
              Complete profile <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>

          {/* Quick actions */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="hub-card rounded-2xl p-6 lg:col-span-2" data-testid="quick-actions">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">Quick actions</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Link to="/app/profile/preview" data-testid="qa-view-profile" className="group flex items-center justify-between rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-4 transition-all hover:-translate-y-0.5 hover:border-[color:var(--ink)]">
                <span className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[color:var(--soft)]"><Eye className="h-4 w-4" /></span><span className="text-sm font-semibold text-[color:var(--ink)]">View my profile</span></span>
                <ArrowUpRight className="h-4 w-4 text-[color:var(--ink-soft)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/app/profile" data-testid="qa-edit-profile" className="group flex items-center justify-between rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-4 transition-all hover:-translate-y-0.5 hover:border-[color:var(--ink)]">
                <span className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[color:var(--soft)]"><Pencil className="h-4 w-4" /></span><span className="text-sm font-semibold text-[color:var(--ink)]">Edit profile</span></span>
                <ArrowUpRight className="h-4 w-4 text-[color:var(--ink-soft)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              {[
                { label: "Explore Businesses", icon: Building2, to: "/app/businesses" },
                { label: "Find Professionals", icon: Users, to: "/app/professionals" },
                { label: "Explore Events", icon: CalendarDays, to: "/app/events" },
                { label: "Discover Opportunities", icon: Target, soon: true },
              ].map((qa) => {
                const Icon = qa.icon;
                if (qa.soon) {
                  return (
                    <div key={qa.label} data-testid={`qa-soon-${qa.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="flex cursor-not-allowed items-center justify-between rounded-xl border border-dashed border-[color:var(--hairline)] bg-[color:var(--soft)]/60 p-4">
                      <span className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[color:var(--surface)] text-[color:var(--ink-soft)]"><Icon className="h-4 w-4" /></span><span className="text-sm font-medium text-[color:var(--ink-soft)]">{qa.label}</span></span>
                      <span className="rounded-full bg-[color:var(--surface)] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Soon</span>
                    </div>
                  );
                }
                return (
                  <Link key={qa.label} to={qa.to} data-testid={`qa-link-${qa.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group flex items-center justify-between rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-4 transition-all hover:-translate-y-0.5 hover:border-[color:var(--ink)]">
                    <span className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[color:var(--soft)]"><Icon className="h-4 w-4" /></span><span className="text-sm font-semibold text-[color:var(--ink)]">{qa.label}</span></span>
                    <ArrowUpRight className="h-4 w-4 text-[color:var(--ink-soft)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Recommendations */}
        <section className="mt-10">
          <h2 className="font-display text-xl font-semibold text-[color:var(--ink)]">Recommended for you</h2>
          <div className="mt-4">
            <EmptySection
              title="Recommendations are coming as the Hub grows."
              description="Future recommendations will draw on your interests, industry, location, what you offer and what you're looking for."
              testId="section-recommendations" icon={Sparkles}
            />
          </div>
        </section>

        {/* Connections & Interests + Events */}
        <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-[color:var(--ink)]">Connections & Interests</h2>
              <Link to="/app/interests" data-testid="home-interests-see-all" className="text-xs font-semibold text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]">View all →</Link>
            </div>
            <div className="hub-card mt-4 rounded-2xl p-6" data-testid="section-interests">
              <div className="flex flex-wrap items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--brand-teal)]/10 text-[color:var(--brand-teal)]">
                  <Handshake className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[color:var(--ink-soft)]">
                    Every connection starts with a clear business intent — partnership, cross-selling, mentorship or more.
                  </p>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface)] p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">Received</p>
                  <p className="font-display mt-1 text-3xl font-semibold text-[color:var(--ink)]" data-testid="home-interests-received-count">{interestCounts.received}</p>
                </div>
                <div className="rounded-xl border border-[color:var(--brand-orange)]/30 bg-[color:var(--brand-orange)]/5 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-orange)]">Pending</p>
                  <p className="font-display mt-1 text-3xl font-semibold text-[color:var(--brand-orange)]" data-testid="home-interests-pending-count">{interestCounts.pending}</p>
                </div>
              </div>
              <Link
                to="/app/interests"
                data-testid="home-view-interests-cta"
                className="group mt-5 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
              >
                View interests <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-[color:var(--ink)]">Upcoming professional events</h2>
              <Link to="/app/events" data-testid="home-events-see-all" className="text-xs font-semibold text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]">See all →</Link>
            </div>
            {upcomingEvents.length === 0 ? (
              <div className="mt-4"><EmptySection title="No events scheduled yet." description="Members will be able to discover and publish events across seminars, conferences, meetups and more." testId="section-events-empty" icon={CalendarDays} /></div>
            ) : (
              <div className="mt-4 space-y-3" data-testid="section-events-list">
                {upcomingEvents.map((e) => (
                  <Link key={e.id} to={`/app/events/${e.id}`} data-testid={`home-event-${e.id}`}
                    className="hub-card group flex items-center gap-4 rounded-2xl p-4 transition-all hover:-translate-y-0.5">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[color:var(--soft)]">
                      {e.image ? <img src={e.image} alt="" className="h-full w-full object-cover" /> : <CalendarDays className="h-5 w-5 text-[color:var(--ink-soft)]" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-teal)]">{formatShort(e.start_datetime)} · {e.event_type}</p>
                      <p className="font-display truncate text-base font-semibold text-[color:var(--ink)]">{e.title}</p>
                      <p className="text-xs text-[color:var(--ink-soft)]"><MapPin className="mr-1 inline h-3 w-3" />{[e.city, e.country].filter(Boolean).join(", ") || e.format}</p>
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-[color:var(--ink-soft)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Community */}
        <section className="mt-10">
          <h2 className="font-display text-xl font-semibold text-[color:var(--ink)]">From the community</h2>
          <div className="mt-4"><EmptySection title="Community launches later this phase." description="Real, business-only discussions across sales, marketing, funding, tech and more — not social noise." testId="section-community" icon={MessagesSquare} /></div>
        </section>

        {/* Hub Journey */}
        <section className="mt-10" data-testid="section-journey">
          <h2 className="font-display text-xl font-semibold text-[color:var(--ink)]">Your Hub journey</h2>
          <p className="mt-2 text-sm text-[color:var(--ink-soft)]">Discover → Connect → Collaborate → Grow</p>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {JOURNEY.map((j) => {
              const Icon = TIMELINE_ICON[j.state];
              const color = j.state === "done" ? "var(--brand-green)" : j.state === "active" ? "var(--brand-teal)" : "var(--ink-soft)";
              const stateLabel = j.state === "done" ? "Done" : j.state === "active" ? "Active" : "Coming soon";
              return (
                <div key={j.label} className="hub-card flex items-center gap-3 rounded-2xl p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`, color }}><Icon className="h-4 w-4" /></span>
                  <div>
                    <p className="text-sm font-semibold text-[color:var(--ink)]">{j.label}</p>
                    <p className="text-[11px] text-[color:var(--ink-soft)]">{stateLabel}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </AppLayout>
  );
}
